import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { TaskCard } from "../../components/vc/TaskCard";
import { TaskStatusSheet } from "../../components/vc/TaskStatusSheet";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/ui/password-input";
import { Label } from "../../components/ui/label";
import {
  ArrowLeft, Phone, Briefcase, Building2, ClipboardList, MessageCircle, KeyRound, Copy, Pencil, ImageOff, Trash2, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../../lib/phone";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

const PRIORITY_TONE = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-sky-100 text-sky-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

export default function VCEmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [statusTask, setStatusTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ email: "", phone: "", password: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const photoFileRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiClient.get("/employees").then((r) => r.data).catch(() => []),
      apiClient.get("/tasks").then((r) => r.data).catch(() => []),
    ]).then(([emps, allTasks]) => {
      const e = (emps || []).find((x) => x.id === id);
      if (!e) { toast.error("Employee not found"); navigate("/employees"); return; }
      setEmployee(e);
      setTasks((allTasks || []).filter((t) => t.employee_id === id));
    }).finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const { pending, inProgress, completed, cancelled } = useMemo(() => {
    const p = [], ip = [], c = [], cx = [];
    tasks.forEach((t) => {
      const s = (t.status || "").toLowerCase();
      if (s === "cancelled") cx.push(t);
      else if (s === "done" || s === "completed") c.push(t);
      else if (s === "in_progress") ip.push(t);
      else p.push(t);
    });
    // sort each by deadline asc / created desc
    const sortByDue = (a, b) => (a.deadline || "9999") < (b.deadline || "9999") ? -1 : 1;
    return { pending: p.sort(sortByDue), inProgress: ip.sort(sortByDue), completed: c.reverse(), cancelled: cx.reverse() };
  }, [tasks]);

  const sendInvite = async () => {
    try {
      const { data } = await apiClient.post(`/employees/${id}/invite`);
      setInvite(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const whatsappInvite = () => {
    if (!invite || !employee) return;
    const msg = `Hi ${employee.name}! Join KonexoCRM as ${employee.role}. Link: ${invite.accept_url} · Code: ${invite.code}`;
    const url = buildWhatsAppUrl(employee.phone, msg);
    if (!url) { toast.error("This employee's phone number looks invalid"); return; }
    window.open(url, "_blank");
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (loading || !employee) {
    return (
      <>
        <Header />
        <div className="px-4 pt-4 pb-28">
          <SkeletonList count={4} height="h-24" />
        </div>
      </>
    );
  }

  const initial = (employee.name || "?").charAt(0).toUpperCase();
  const inactiveByOwner = (employee.status || "active") === "inactive";
  const active = !!employee.has_account && !inactiveByOwner;

  const toggleActive = async () => {
    const next = inactiveByOwner ? "active" : "inactive";
    try {
      await apiClient.put(`/employees/${id}`, { status: next });
      toast.success(next === "active" ? "Employee marked active" : "Employee deactivated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  // Phase 5 #5 — admin can edit email/mobile/password from the profile page;
  // changes apply immediately to the employee's own login/profile since both
  // sides read the same `employees` record.
  const openEdit = () => {
    setEditForm({ email: employee.email || "", phone: employee.phone || "", password: "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const changes = {};
      if (editForm.email.trim() !== (employee.email || "")) changes.email = editForm.email.trim();
      if (editForm.phone.trim() !== (employee.phone || "")) changes.phone = editForm.phone.trim();
      if (Object.keys(changes).length > 0) {
        await apiClient.put(`/employees/${id}`, changes);
      }
      if (editForm.password.trim()) {
        await apiClient.put(`/employees/${id}/password`, { new_password: editForm.password.trim() });
      }
      toast.success("Profile updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };

  const removePhoto = async () => {
    try {
      await apiClient.delete(`/employees/${id}/photo`);
      toast.success("Photo removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  // Phase 7 #7 — admin can upload/change the employee's photo too; same
  // base64-in-Mongo field the employee's own upload writes to.
  const onPhotoFile = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3MB"); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setUploadingPhoto(true);
      try {
        await apiClient.put(`/employees/${id}`, { profile_photo: ev.target.result });
        toast.success("Photo updated");
        load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Phase 6 #1-3 — soft delete only, never a hard delete; the record is kept
  // for reactivation from the Deleted Employees page.
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.post(`/employees/${id}/delete`);
      toast.success("Employee deleted");
      navigate("/employees");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
      setDeleting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-5">
        <button
          onClick={() => navigate("/employees")}
          data-testid="employee-detail-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Profile card */}
        <div data-testid="employee-profile-card" className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
          <div className="flex items-start gap-4">
            {employee.profile_photo ? (
              <img
                src={employee.profile_photo}
                alt={employee.name}
                data-testid="employee-profile-photo"
                className="w-16 h-16 rounded-2xl object-cover shrink-0 border border-slate-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700 grid place-items-center shrink-0 font-bold text-xl">
                {initial}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-lg font-bold text-slate-900">{employee.name}</div>
                <StatusBadge
                  status={active ? "active" : inactiveByOwner ? "closed" : "inactive"}
                  label={active ? "Active" : inactiveByOwner ? "Inactive" : "Pending"}
                  testid="employee-profile-status"
                />
              </div>
              {employee.employee_code && (
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="employee-code">
                  ID · {employee.employee_code}
                </div>
              )}
              <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> {employee.designation || employee.role || "Staff"}
              </div>
              {employee.email && (
                <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5 break-all" data-testid="employee-profile-email">
                  <Building2 className="w-3 h-3" /> {employee.email}
                </div>
              )}
              {employee.phone && (
                <a
                  href={`tel:${employee.phone}`}
                  data-testid="employee-profile-phone"
                  className="text-xs text-emerald-700 flex items-center gap-1.5 mt-1 hover:underline"
                >
                  <Phone className="w-3 h-3" /> {employee.phone}
                </a>
              )}
            </div>
            <button
              onClick={openEdit}
              data-testid="employee-edit-btn"
              className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 grid place-items-center shrink-0"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
            <StatMini label="Pending" value={pending.length} tone="amber" testid="stat-pending" />
            <StatMini label="In Progress" value={inProgress.length} tone="sky" testid="stat-in-progress" />
            <StatMini label="Completed" value={completed.length} tone="emerald" testid="stat-completed" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <SecondaryButton
              onClick={() => navigate(`/tasks/assign?employee_id=${employee.id}`)}
              data-testid="employee-detail-assign"
              className="flex items-center justify-center gap-1 h-11"
            >
              <ClipboardList className="w-4 h-4" /> Assign Task
            </SecondaryButton>
            {!active && !inactiveByOwner ? (
              <PrimaryButton onClick={sendInvite} data-testid="employee-detail-invite" className="flex items-center justify-center gap-1 h-11">
                <MessageCircle className="w-4 h-4" /> Send Invite
              </PrimaryButton>
            ) : employee.phone ? (
              <PrimaryButton
                onClick={() => {
                  const url = buildWhatsAppUrl(employee.phone);
                  if (!url) { toast.error("This employee's phone number looks invalid"); return; }
                  window.open(url, "_blank");
                }}
                data-testid="employee-detail-wa"
                className="flex items-center justify-center gap-1 h-11"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </PrimaryButton>
            ) : null}
          </div>
          {/* #16 — Active / Inactive toggle */}
          <button
            onClick={toggleActive}
            data-testid="employee-toggle-status"
            className={`mt-3 w-full h-10 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition ${
              inactiveByOwner
                ? "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200"
                : "bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200"
            }`}
          >
            {inactiveByOwner ? "Reactivate employee" : "Mark inactive"}
          </button>

          {/* Phase 6 #1 — Delete Employee (Admin only, soft delete) */}
          <button
            onClick={() => setDeleteOpen(true)}
            data-testid="employee-delete-btn"
            className="mt-2 w-full h-10 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 transition"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete Employee
          </button>
        </div>

        {/* Pending tasks */}
        <Section title="Pending Tasks" subtitle={`${pending.length} to do`} testid="section-pending">
          {pending.length === 0 ? (
            <EmptyState Icon={ClipboardList} title="No pending tasks" subtitle="Assign a task using the button above" />
          ) : (
            <div className="space-y-2" data-testid="pending-tasks-list">
              {pending.map((t) => <TaskRow key={t.id} task={t} onClick={() => setStatusTask(t)} testid={`pending-task-${t.id}`} />)}
            </div>
          )}
        </Section>

        {/* In progress */}
        {inProgress.length > 0 && (
          <Section title="In Progress" subtitle={`${inProgress.length} working`} testid="section-in-progress">
            <div className="space-y-2" data-testid="in-progress-tasks-list">
              {inProgress.map((t) => <TaskRow key={t.id} task={t} onClick={() => setStatusTask(t)} testid={`in-progress-task-${t.id}`} />)}
            </div>
          </Section>
        )}

        {/* Completed tasks */}
        <Section title="Completed Tasks" subtitle={`${completed.length} done`} testid="section-completed">
          {completed.length === 0 ? (
            <EmptyState Icon={ClipboardList} title="No completed tasks yet" subtitle="Completed work will show up here" />
          ) : (
            <div className="space-y-2" data-testid="completed-tasks-list">
              {completed.slice(0, 20).map((t) => <TaskRow key={t.id} task={t} onClick={() => setStatusTask(t)} testid={`completed-task-${t.id}`} />)}
            </div>
          )}
        </Section>

        {/* Cancelled tasks */}
        {cancelled.length > 0 && (
          <Section title="Cancelled Tasks" subtitle={`${cancelled.length} cancelled`} testid="section-cancelled">
            <div className="space-y-2" data-testid="cancelled-tasks-list">
              {cancelled.slice(0, 20).map((t) => <TaskRow key={t.id} task={t} onClick={() => setStatusTask(t)} testid={`cancelled-task-${t.id}`} />)}
            </div>
          </Section>
        )}
      </div>

      {/* Invite share */}
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle>Invite {employee.name}</DialogTitle>
            <DialogDescription>Share this link or code. Expires in 7 days.</DialogDescription>
          </DialogHeader>
          {invite && (
            <div className="space-y-3">
              <div data-testid="invite-link" className="bg-slate-50 rounded-xl p-3 text-xs font-mono break-all border border-slate-200">
                {invite.accept_url}
              </div>
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
                <KeyRound className="w-4 h-4 text-indigo-700" />
                <span data-testid="invite-code" className="font-mono font-bold text-indigo-900 flex-1">{invite.code}</span>
                <button onClick={() => copyText(invite.code)} data-testid="invite-copy-code" className="text-indigo-700 hover:text-indigo-900">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SecondaryButton onClick={() => copyText(invite.accept_url)} data-testid="invite-copy-link" className="flex items-center justify-center gap-1">
                  <Copy className="w-4 h-4" /> Copy link
                </SecondaryButton>
                <PrimaryButton onClick={whatsappInvite} data-testid="invite-wa" className="flex items-center justify-center gap-1">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit profile — email, phone, password, remove photo (Phase 5 #5) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="employee-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
            <DialogDescription>Changes apply immediately to {employee.name}'s login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Email ID</Label>
              <Input data-testid="employee-edit-email" type="email" value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="h-12 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile Number</Label>
              <Input data-testid="employee-edit-phone" type="tel" value={editForm.phone}
                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className="h-12 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <PasswordInput data-testid="employee-edit-password" value={editForm.password}
                onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Leave blank to keep current" minLength={6} className="h-12 rounded-xl" />
            </div>
            {employee.profile_photo && (
              <button
                onClick={removePhoto}
                data-testid="employee-remove-photo-btn"
                className="w-full h-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold flex items-center justify-center gap-1.5"
              >
                <ImageOff className="w-4 h-4" /> Remove profile photo
              </button>
            )}
            <button
              onClick={() => photoFileRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="employee-upload-photo-btn"
              className="w-full h-11 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              <Upload className="w-4 h-4" /> {employee.profile_photo ? "Change" : "Upload"} profile photo
            </button>
            <input ref={photoFileRef} type="file" accept="image/*" onChange={onPhotoFile} data-testid="employee-photo-input" className="hidden" />
            <div className="grid grid-cols-2 gap-2 pt-1">
              <SecondaryButton onClick={() => setEditOpen(false)} data-testid="employee-edit-cancel">Cancel</SecondaryButton>
              <PrimaryButton onClick={saveEdit} loading={editSaving} data-testid="employee-edit-save">Save</PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TaskStatusSheet
        task={statusTask}
        open={!!statusTask}
        onOpenChange={(o) => !o && setStatusTask(null)}
        onUpdated={() => load()}
      />

      {/* Delete confirmation (Phase 6 #2) */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="employee-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Employee?</DialogTitle>
            <DialogDescription>Are you sure you want to delete this employee?</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            {employee.name} will lose login access and disappear from your active team, but their details are kept and can be restored anytime from Deleted Employees.
          </p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <SecondaryButton onClick={() => setDeleteOpen(false)} data-testid="employee-delete-cancel">
              No / Cancel
            </SecondaryButton>
            <button
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="employee-delete-confirm"
              className="h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              {deleting ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatMini({ label, value, tone = "slate", testid }) {
  const tones = {
    amber: "text-amber-700 bg-amber-50",
    sky: "text-sky-700 bg-sky-50",
    emerald: "text-emerald-700 bg-emerald-50",
    slate: "text-slate-700 bg-slate-50",
  };
  const cls = tones[tone] || tones.slate;
  return (
    <div data-testid={testid} className={`rounded-2xl p-2.5 text-center ${cls.split(" ")[1]}`}>
      <div className={`text-xl font-bold leading-none ${cls.split(" ")[0]}`}>{value}</div>
      <div className={`text-[9px] uppercase tracking-wider font-bold mt-1 ${cls.split(" ")[0]}`}>{label}</div>
    </div>
  );
}

function statusToKey(status) {
  const s = (status || "").toLowerCase();
  if (s === "done" || s === "completed") return "completed";
  if (s === "in_progress") return "in_progress";
  if (s === "cancelled") return "closed";
  return "pending";
}

function TaskRow({ task, testid, onClick }) {
  const prio = (task.priority || "").toLowerCase();
  const tone = PRIORITY_TONE[prio];
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left bg-white border border-slate-100 rounded-2xl p-3 shadow-sm ${onClick ? "hover:shadow transition active:scale-[0.99] cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 text-sm">{task.title}</div>
          {task.description && (
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{task.description}</div>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            {tone && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>
                {prio}
              </span>
            )}
            {task.deadline && (
              <span className="text-[11px] text-slate-500">
                Due {new Date(task.deadline).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
              </span>
            )}
          </div>
        </div>
        <StatusBadge status={statusToKey(task.status)} />
      </div>
    </Wrapper>
  );
}

// Re-export TaskCard so callers that expect the layout helper can use it too
export { TaskCard };
